import { useState, useCallback, useRef, useEffect } from 'react';
import appConfigApi, {
    ChangeRequest,
    ChangeRequestHistoryEntry,
} from '../../services/AppConfigDeployer/AppConfigDeployerService';
import { DeployerRow } from './types';

export function useAppConfigDeployer(stage: string, region: string, userAlias: string) {
    const [deployerRows, setDeployerRows] = useState<DeployerRow[]>([]);
    const [loadingDeployer, setLoadingDeployer] = useState(false);
    const [deployerError, setDeployerError] = useState<string | null>(null);
    const [deployerSuccess, setDeployerSuccess] = useState<string | null>(null);
    const [actionLoading, setActionLoading] = useState<string | null>(null);
    const [showRejectModal, setShowRejectModal] = useState<string | null>(null);
    const [copiedId, setCopiedId] = useState<string | null>(null);
    const [reviewDiffChangeId, setReviewDiffChangeId] = useState<string | null>(null);
    const [reviewDiffLoading, setReviewDiffLoading] = useState(false);
    const [reviewDiffOldData, setReviewDiffOldData] = useState<any>({});
    const [reviewDiffNewData, setReviewDiffNewData] = useState<any>({});

    // Ref for setTimeout cleanup to prevent memory leaks
    const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (copyTimeoutRef.current) {
                clearTimeout(copyTimeoutRef.current);
            }
        };
    }, []);

    const safeParseJson = (raw: string): any => {
        let parsed = JSON.parse(raw);
        if (typeof parsed === 'string') parsed = JSON.parse(parsed);
        return parsed;
    };

    const loadDeployerData = useCallback(async () => {
        setLoadingDeployer(true);
        setDeployerError(null);
        try {
            const [pending, history] = await Promise.all([
                appConfigApi.listChangeRequests(stage, region).catch(() => [] as ChangeRequest[]),
                appConfigApi.getHistory(stage, region).catch(() => [] as ChangeRequestHistoryEntry[]),
            ]);

            const pendingRows: DeployerRow[] = pending.map(cr => ({
                changeId: cr.changeId, requestor: cr.requestor,
                reviewer: cr.assignedReviewer || '—', createdAt: cr.createdAt,
                region: cr.region, useCase: cr.useCase, fromVersion: cr.fromVersion,
                toVersion: 0, status: 'WAITING_FOR_APPROVAL' as const,
                isPending: true, proposedConfig: cr.proposedConfig,
            }));

            const historyRows: DeployerRow[] = history.map(h => ({
                changeId: h.changeId, requestor: h.requestor,
                reviewer: h.approver || h.assignedReviewer || '—', createdAt: h.createdAt,
                region: h.region, useCase: h.useCase, fromVersion: h.fromVersion,
                toVersion: h.toVersion, status: h.status as DeployerRow['status'],
                resolvedAt: h.resolvedAt, rejectionReason: h.rejectionReason || undefined,
                isPending: false, proposedConfig: h.proposedConfig || undefined,
            }));

            setDeployerRows([
                ...pendingRows.sort((a, b) => b.createdAt.localeCompare(a.createdAt)),
                ...historyRows.sort((a, b) => (b.resolvedAt || b.createdAt).localeCompare(a.resolvedAt || a.createdAt)),
            ]);
        } catch (e: any) {
            console.error('loadDeployerData error:', e);
            setDeployerError('Failed to load change requests.');
        } finally {
            setLoadingDeployer(false);
        }
    }, [stage, region]);

    const handleViewDiff = async (row: DeployerRow) => {
        if (!row.proposedConfig) {
            setDeployerError('No proposed config available for this change request.');
            return;
        }
        setReviewDiffLoading(true);
        setReviewDiffChangeId(row.changeId);
        setDeployerError(null);
        try {
            const result = await appConfigApi.getConfig(stage, region, row.useCase, row.fromVersion);
            const currentContent = result?.content ?? '{}';
            setReviewDiffOldData(typeof currentContent === 'string' ? safeParseJson(currentContent) : currentContent);
            setReviewDiffNewData(safeParseJson(row.proposedConfig));
        } catch (e: any) {
            console.error('handleViewDiff error:', e);
            setDeployerError('Failed to load config diff. ' + (e?.message || ''));
            setReviewDiffChangeId(null);
        } finally {
            setReviewDiffLoading(false);
        }
    };

    const handleShareLink = (changeId: string) => {
        const url = `${window.location.origin}${window.location.pathname}?review=${changeId}&stage=${encodeURIComponent(stage)}&region=${encodeURIComponent(region)}`;
        const onCopied = () => {
            setCopiedId(changeId);
            if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
            copyTimeoutRef.current = setTimeout(() => setCopiedId(null), 2000);
        };
        navigator.clipboard.writeText(url).then(onCopied).catch(() => {
            const el = document.createElement('textarea');
            el.value = url;
            document.body.appendChild(el);
            el.select();
            document.execCommand('copy');
            document.body.removeChild(el);
            onCopied();
        });
    };

    const handleApprove = async (changeId: string, requestor: string) => {
        if (!userAlias) return;
        if (userAlias === requestor) {
            setDeployerError('You cannot approve your own change request. Please ask another team member to review.');
            return;
        }
        setActionLoading(changeId);
        setDeployerError(null);
        setDeployerSuccess(null);
        let succeeded = false;
        try {
            await appConfigApi.approveChangeRequest(stage, region, changeId, userAlias);
            setDeployerSuccess(`Change request ${changeId.slice(0, 8)}… approved and deployed successfully.`);
            // Optimistic UI update — immediately mark as approved to prevent "still showing pending" bug
            setDeployerRows(prev => prev.map(r =>
                r.changeId === changeId
                    ? { ...r, isPending: false, status: 'APPROVED' as const, reviewer: userAlias, resolvedAt: new Date().toISOString() }
                    : r
            ));
            succeeded = true;
        } catch (e: any) {
            setDeployerError(`Approval failed: ${e?.message || 'Failed to approve change request'}. The config was NOT deployed.`);
        } finally {
            setActionLoading(null);
        }
        // Background refresh — failure here is non-critical
        if (succeeded) {
            loadDeployerData().catch(() => {});
        }
    };

    const handleReject = async (changeId: string, requestor: string) => {
        if (!userAlias) return;
        setActionLoading(changeId);
        setDeployerError(null);
        setDeployerSuccess(null);
        let succeeded = false;
        try {
            await appConfigApi.rejectChangeRequest(stage, region, changeId, userAlias, 'Rejected');
            setDeployerSuccess(`Change request ${changeId.slice(0, 8)}… rejected.`);
            setShowRejectModal(null);
            setDeployerRows(prev => prev.filter(r => r.changeId !== changeId));
            succeeded = true;
        } catch (e: any) {
            setDeployerError(`Rejection failed: ${e?.message || 'Failed to reject change request'}`);
        } finally {
            setActionLoading(null);
        }
        // Background refresh — failure here is non-critical
        if (succeeded) {
            loadDeployerData().catch(() => {});
        }
    };

    const closeDiffModal = () => {
        setReviewDiffChangeId(null);
        setReviewDiffOldData({});
        setReviewDiffNewData({});
    };

    return {
        deployerRows, loadingDeployer, deployerError, deployerSuccess,
        actionLoading, showRejectModal, setShowRejectModal, copiedId,
        reviewDiffChangeId, reviewDiffLoading, reviewDiffOldData, reviewDiffNewData,
        loadDeployerData, handleViewDiff, handleShareLink, handleApprove, handleReject,
        closeDiffModal,
    };
}
